import { isPlainObject } from 'lodash';
import React, { useEffect, useState } from 'react';

import { useSupportEligibility, ZapType } from '@zapier/product-get-help';
import { addLocationOriginToUrl } from '@zapier/url-utils';

import { createId } from 'app/common/CommonUtils';

import useCurrentAccount, {
  SelectedAccount,
} from 'app/get-help/hooks/useCurrentAccount';
import useCurrentUser, { CurrentUser } from 'app/get-help/hooks/useCurrentUser';
import { queryToDataSet } from 'app/get-help/utils/tree';

type DataSet = {
  [key: string]: string | number | number[]; // TODO: Drop array
};

export type SizeType = 'small' | 'medium' | 'large';

type Context = {
  account?: SelectedAccount;
  attemptId: string;
  doc?: string;
  from_url?: string;
  initialDataSet: DataSet;
  isEmailSupportEligible: boolean;
  isLoading: boolean;
  isPaidEmailSupportEligible: boolean;
  isPremierSupportEligible: boolean;
  isCriticalTicketEligible: boolean;
  lastTrackedPath: string;
  showLoginComponent: boolean;
  setLastTrackedPath: (path: string) => void;
  setShowLoginComponent: (value: boolean) => void;
  setZap: (zap?: ZapType) => void;
  size: SizeType;
  user?: CurrentUser;
  zap?: ZapType;
  isCriticalIssue: boolean;
  setIsCriticalIssue: (value: boolean) => void;
};

type Query = {
  [key: string]: string;
};

type FormProviderProps = {
  children: React.ReactChild[] | ((value: Context) => React.ReactNode);
  query: Query;
  size: SizeType;
};

const HISTORY_RE = /\/app\/history\/[a-z0-9-]+/;
const EDITOR_RE = /\/app\/editor\/([0-9]+)/;

export const parseReferrerTag = () => {
  const currentURL = window.location.href;
  try {
    // Create a URL object from the current URL
    const url = new URL(currentURL);
    const referrerTag = url.searchParams.get('referrerTag');

    if (referrerTag && ['pnp-faq', 'pnp-faq-edge'].includes(referrerTag)) {
      return referrerTag;
    }
  } catch (error) {
    // Handle URL parsing errors, if any
    // eslint-disable-next-line no-console
    console.error('Error parsing URL:', error);
  }
  // Return undefined if referrerTag is not present or doesn't match a valid value
  return undefined;
};

const parseQuery = (
  query: Query
): {
  doc?: string;
  from_url?: string;
  initialDataSet: DataSet;
} => {
  let doc: string, from_url: string;

  const queryToParse = { ...query };

  if (queryToParse.doc) {
    doc = addLocationOriginToUrl(queryToParse.doc);
    delete queryToParse.doc;
  }

  if (queryToParse.from_url) {
    from_url = addLocationOriginToUrl(queryToParse.from_url);
    delete queryToParse.from_url;
  }

  if (queryToParse.error) {
    queryToParse.topic = 'troubleshooting';
    queryToParse.problem = 'error';
  }

  const initialDataSet = queryToDataSet(queryToParse);

  // derive fields from `from_url`
  if (from_url) {
    if (
      (!isPlainObject(initialDataSet.help) || !initialDataSet.help.task) &&
      HISTORY_RE.test(from_url)
    ) {
      initialDataSet.help = initialDataSet.help || {};
      initialDataSet.help.task = from_url;
    } else if (!initialDataSet.zap) {
      const match = from_url.match(EDITOR_RE);
      if (match) {
        initialDataSet.zap = match[1];
      }
    }
  }

  return {
    doc,
    from_url,
    initialDataSet,
  };
};

const ContextMissing = () => {
  throw new Error('FormContext not provided!');
};

const FormContext = React.createContext<Context>({
  account: undefined,
  attemptId: createId(),
  doc: undefined,
  from_url: undefined,
  initialDataSet: {},
  isEmailSupportEligible: undefined,
  isPaidEmailSupportEligible: undefined,
  isLoading: false,
  isPremierSupportEligible: undefined,
  isCriticalTicketEligible: undefined,
  lastTrackedPath: undefined,
  showLoginComponent: true,
  setLastTrackedPath: ContextMissing,
  setShowLoginComponent: ContextMissing,
  setZap: ContextMissing,
  size: undefined,
  user: undefined,
  zap: undefined,
  isCriticalIssue: false,
  setIsCriticalIssue: ContextMissing,
});
FormContext.displayName = 'FormContext';

export const FormProvider = ({ children, query, size }: FormProviderProps) => {
  const [lastTrackedPath, setLastTrackedPath] = useState<string>();
  const [zap, setZap] = useState<ZapType>();
  const [showLoginComponent, setShowLoginComponent] = useState<boolean>(true);
  const [isCriticalIssue, setIsCriticalIssue] = useState<boolean>(false);

  const { isLoading: isLoadingUser, user } = useCurrentUser();
  const { isLoading: isLoadingAccount, account } = useCurrentAccount();
  const {
    isLoading: isLoadingEligibility,
    eligibility,
  } = useSupportEligibility();

  useEffect(() => {
    if (account) {
      setShowLoginComponent(false);
    }
  }, [account]);

  const attemptId = createId();

  const { doc, from_url, initialDataSet } = parseQuery(query);

  const value = {
    account,
    attemptId,
    doc,
    from_url,
    initialDataSet,
    // On errors, default to true
    isEmailSupportEligible:
      eligibility && eligibility.isEmailSupportEligible !== false,
    isPaidEmailSupportEligible:
      eligibility && eligibility.isPaidEmailSupportEligible === true,
    isCriticalTicketEligible:
      eligibility && eligibility.isCriticalTicketEligible === true,
    isLoading: isLoadingUser || isLoadingAccount || isLoadingEligibility,
    isPremierSupportEligible:
      eligibility && eligibility.isPremierSupportEligible === true,
    lastTrackedPath,
    setLastTrackedPath,
    setShowLoginComponent,
    showLoginComponent,
    setZap,
    size,
    user,
    zap,
    isCriticalIssue,
    setIsCriticalIssue,
  };

  return (
    <FormContext.Provider value={value}>
      {typeof children === 'function' ? children(value) : children}
    </FormContext.Provider>
  );
};

export const useFormContext = () => React.useContext(FormContext);

export default FormProvider;
